import { ref, isRef, watch, unref } from 'vue';

/**
 * Эта директива автоматически рассчитывает и устанавливает значение `max-height`
 * для элемента, чтобы он оставался в пределах доступной высоты окна с учётом
 * заданного нижнего отступа. Также поддерживается возможность отключения слежения
 * за изменениями размеров окна.
 */

interface HTMLElementWithCleanup extends HTMLElement {
  _cleanup?: () => void; // Метод для очистки обработчиков событий
}

interface MaxHeightOptions {
  /**
   * Отступ от нижнего края окна в пикселях.
   * Например, если вам нужно оставить место для футера.
   */
  bottomOffset?: number;

  /**
   * Управляет включением/выключением директивы:
   * - Если `true`, директива перестаёт пересчитывать `max-height`.
   * - Если реактивное значение (ref), директива будет реагировать на его изменение.
   */
  stop?: boolean | Ref<boolean>;

  /**
   * Дополнительные параметры для расширения функционала.
   */
  [key: string]: any;
}

/**
 * Вспомогательная функция для создания debounce.
 * Предотвращает частые вызовы функции за короткий интервал времени.
 *
 * @param func - Функция, которую нужно обернуть в debounce.
 * @param wait - Задержка в миллисекундах.
 * @returns - Декорированная функция с дебаунсом.
 */
function debounce(func: (...args: any[]) => void, wait: number): (...args: any[]) => void {
  let timeout: number | null = null;

  return (...args: any[]) => {
    if (timeout) clearTimeout(timeout);
    timeout = window.setTimeout(() => func(...args), wait);
  };
}

export default {
  /**
   * Вызывается при монтировании элемента в DOM.
   *
   * @param el - Элемент, к которому применена директива.
   * @param binding - Объект с переданными параметрами.
   */
  mounted(el: HTMLElementWithCleanup, binding: { value: MaxHeightOptions }) {
    const options = binding.value || {};
    const bottomOffset = options.bottomOffset || 0; // Нижний отступ по умолчанию 0.

    // Если значение `stop` передано как boolean, превращаем его в ref.
    const stopRef = isRef(options.stop) ? options.stop : ref(!!options.stop);

    let fixedMaxHeight: number | null = null; // Хранит текущее значение max-height.
    let isTracking = !unref(stopRef); // Автоматическое отслеживание, если stop === false.

    /**
     * Функция пересчёта значения `max-height`.
     * Использует текущую позицию элемента и доступную область окна.
     */
    const calculateMaxHeight = () => {
      const rect = el.getBoundingClientRect(); // Координаты элемента относительно окна.
      const windowHeight = window.innerHeight; // Высота окна браузера.

      // Максимальная доступная высота с учётом нижнего отступа.
      const availableHeight = windowHeight - rect.top - bottomOffset;
      fixedMaxHeight = Math.max(availableHeight, 0); // Убедимся, что значение не отрицательное.
      el.style.maxHeight = `${fixedMaxHeight}px`; // Устанавливаем max-height в CSS.
    };

    /**
     * Обработчик изменения размеров окна.
     * Использует debounce, чтобы избежать избыточных вызовов.
     */
    const resizeHandler = debounce(() => {
      if (isTracking) {
        calculateMaxHeight(); // Обновляем max-height только если отслеживание включено.
      }
    }, 50);

    /**
     * Наблюдатель за изменением значения `stop`.
     * Включает или отключает слежение за изменениями размеров окна.
     */
    const stopWatcher = watch(
      stopRef,
      (newValue: boolean) => {
        isTracking = !newValue; // Переключаем статус слежения.
        if (isTracking) {
          calculateMaxHeight(); // Пересчитываем высоту при включении.
          window.addEventListener('resize', resizeHandler);
        } else {
          window.removeEventListener('resize', resizeHandler); // Убираем обработчик resize.
          el.style.maxHeight = ''; // Очищаем значение max-height при остановке, если это необходимо.
        }
      },
      { immediate: true }, // Выполнить сразу при запуске.
    );

    // Инициализация. Если слежение включено, рассчитываем и добавляем обработчик событий.
    if (isTracking) {
      calculateMaxHeight();
      window.addEventListener('resize', resizeHandler);
    }

    /**
     * Метод очистки ресурсов. Удаляет обработчики событий и наблюдателей.
     * Автоматически вызывается при размонтировании элемента.
     */
    el._cleanup = () => {
      stopWatcher(); // Убираем наблюдатель.
      window.removeEventListener('resize', resizeHandler); // Убираем обработчик resize.
    };
  },

  /**
   * Вызывается при удалении элемента из DOM.
   * Используется для очистки обработчиков событий и наблюдателей.
   *
   * @param el - Элемент, к которому применена директива.
   */
  unmounted(el: HTMLElementWithCleanup) {
    if (el._cleanup) el._cleanup(); // Вызываем метод очистки, если он существует.
  },
};
